import Store from '@/Store';
import { defaultFeatures, overrideFeatures } from '@/Config/Feature';

/**
 * Centralized feature toggle service for managing application features
 *
 * Provides a unified interface for checking feature availability based on
 * configuration fetched from the CMS via the /config/app endpoint.
 *
 * Override priority: Config/Feature.ts overrides > Store state (from API)
 *
 *
 * In Templates:
 *
 * 	<!-- Single feature check -->
 * 	<div v-if="$feature('kit_pad')">...</div>
 *
 * 	<!-- Multiple required features -->
 * 	<div v-if="$feature.all(['auth', 'auth_login'])">...</div>
 *
 * 	<!-- Any of these features -->
 * 	<div v-if="$feature.any(['kit_pad', 'kit_trainer'])">...</div>
 *
 *
 * In Components:
 *
 * 	import * as Service from '@/App/Service';
 *
 * 	if (Service.Feature.isEnabled('kit_pad')) {
 * 	    // Feature is enabled
 * 	}
 *
 * 	// Check dependencies
 * 	const deps = Service.Feature.getDependencies('auth_login'); // ['auth']
 *
 *
 * @author Matt Kenefick <matt@polymermallard.com>
 * @package App/Service
 * @project ChalkySticks Web App
 */
export class Feature {
	/**
	 * Get all dependencies recursively for a feature
	 *
	 * @param string featureName
	 * @return Array<string>
	 */
	public getAllDependencies(featureName: string): Array<string> {
		const visited = new Set<string>();
		const dependencies: Array<string> = [];

		const collectDependencies = (name: string) => {
			if (visited.has(name)) {
				return;
			}

			visited.add(name);

			const deps = this.getDependencies(name);

			deps.forEach((dep) => {
				collectDependencies(dep);

				if (!dependencies.includes(dep)) {
					dependencies.push(dep);
				}
			});
		};

		collectDependencies(featureName);

		return dependencies;
	}

	/**
	 * Get all currently enabled features as an array of feature names
	 *
	 * @return Array<string>
	 */
	public getEnabledFeatures(): Array<string> {
		const features = this.getFeatures();

		return Object.keys(features)
			.filter((key) => features[key] === true)
			.sort();
	}

	/**
	 * Get dependencies for a feature from the default definitions
	 *
	 * @param string featureName
	 * @return Array<string>
	 */
	public getDependencies(featureName: string): Array<string> {
		const feature = defaultFeatures[featureName];

		return feature?.dependencies || [];
	}

	/**
	 * Get features that depend on a given feature
	 *
	 * @param string featureName
	 * @return Array<string>
	 */
	public getDependents(featureName: string): Array<string> {
		const dependents: Array<string> = [];

		Object.entries(defaultFeatures).forEach(([key, metadata]) => {
			if (metadata.dependencies?.includes(featureName)) {
				dependents.push(key);
			}
		});

		return dependents.sort();
	}

	/**
	 * Get all currently disabled features as an array of feature names
	 *
	 * @return Array<string>
	 */
	public getDisabledFeatures(): Array<string> {
		const features = this.getFeatures();

		return Object.keys(features)
			.filter((key) => features[key] === false)
			.sort();
	}

	/**
	 * Get the raw features object from the store
	 *
	 * @return IFeatures
	 */
	public getFeatures(): IFeatures {
		return Store.state.settings.features || {};
	}

	/**
	 * Get feature category from feature name
	 * E.g., 'auth_login' returns 'auth', 'kit_pad' returns 'kit'
	 *
	 * @param string featureName
	 * @return string
	 */
	public getFeatureCategory(featureName: any): string {
		const parts = featureName.split('_');

		return parts[0] || 'general';
	}

	/**
	 * Get a human-readable label for a feature name
	 * Converts snake_case to Title Case
	 *
	 * @param string featureName
	 * @return string
	 */
	public getFeatureLabel(featureName: any): string {
		return featureName
			.split('_')
			.map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
			.join(' ');
	}

	/**
	 * Get all features grouped by category
	 *
	 * @return Record<string, Array<string>>
	 */
	public getFeaturesByCategory(): Record<string, Array<string>> {
		const features = this.getFeatures();
		const categories: Record<string, Array<string>> = {};

		Object.keys(features).forEach((featureName) => {
			const category = this.getFeatureCategory(featureName);

			if (!categories[category]) {
				categories[category] = [];
			}

			categories[category].push(featureName);
		});

		// Sort features within each category
		Object.keys(categories).forEach((category) => {
			categories[category].sort();
		});

		return categories;
	}

	/**
	 * Get feature metadata from the default definitions
	 *
	 * @param string featureName
	 * @return IFeatureMetadata | undefined
	 */
	public getMetadata(featureName: string): IFeatureMetadata | undefined {
		return defaultFeatures[featureName];
	}

	/**
	 * Check if ALL specified features are enabled
	 *
	 * @param Array<string> featureNames
	 * @return boolean
	 */
	public hasAll(featureNames: Array<string>): boolean {
		return featureNames.every((name) => this.isEnabled(name));
	}

	/**
	 * Check if ANY of the specified features are enabled
	 *
	 * @param Array<string> featureNames
	 * @return boolean
	 */
	public hasAny(featureNames: Array<string>): boolean {
		return featureNames.some((name) => this.isEnabled(name));
	}

	/**
	 * Check if NONE of the specified features are enabled
	 *
	 * @param Array<string> featureNames
	 * @return boolean
	 */
	public hasNone(featureNames: Array<string>): boolean {
		return !this.hasAny(featureNames);
	}

	/**
	 * Check if a single feature is enabled
	 *
	 * Overrides from Config/Feature.ts take highest priority.
	 * If no override is set (null/undefined), falls back to store state.
	 *
	 * @param string featureName
	 * @return boolean
	 */
	public isEnabled(featureName: string): boolean {
		// Check for override first
		const override = overrideFeatures[featureName];

		if (override != null) {
			return override;
		}

		const features = this.getFeatures();

		return features[featureName] === true;
	}

	/**
	 * Check if a feature and all its dependencies are enabled
	 *
	 * @param string featureName
	 * @return boolean
	 */
	public isFullyEnabled(featureName: string): boolean {
		// Check the feature itself
		if (!this.isEnabled(featureName)) {
			return false;
		}

		// Check all dependencies recursively
		const dependencies = this.getAllDependencies(featureName);

		if (dependencies.length > 0) {
			return this.hasAll(dependencies);
		}

		return true;
	}

	/**
	 * Validate that all enabled features have their dependencies met
	 *
	 * @return Array<string> - Array of validation errors
	 */
	public validateFeatures(): Array<string> {
		const features = this.getFeatures();
		const errors: Array<string> = [];

		Object.entries(features).forEach(([featureName, isEnabled]) => {
			if (!isEnabled) {
				return;
			}

			const dependencies = this.getDependencies(featureName);

			dependencies.forEach((dep) => {
				if (!features[dep]) {
					const feature = defaultFeatures[featureName];
					const depFeature = defaultFeatures[dep];

					errors.push(
						`Feature "${feature?.description}" requires "${depFeature?.description}" to be enabled`,
					);
				}
			});
		});

		return errors;
	}
}

/**
 * Build default feature values from Config definitions for store initialization
 *
 * @return Record<string, boolean>
 */
export function getDefaultFeatures(): Record<string, boolean> {
	const defaults: Record<string, boolean> = {};

	Object.entries(defaultFeatures).forEach(([key, metadata]) => {
		defaults[key] = metadata.default;
	});

	return defaults;
}

// Export singleton instance
export default new Feature();
